<template>
  <!-- "Woran arbeitest du?" for the home Stempeluhr: Zeit's project chooser
       (time-ui StartProjectChooser) as a sheet that rises from the bottom edge on
       a phone -- one tap for the project, one for the Tätigkeit, rows tall enough
       for a thumb in a work glove. Step two only when there is a Tätigkeit to
       choose (needsActivityStep). -->
  <Modal
    :open="open"
    sheet
    :title="step === 'activity' && chosen ? chosen.name : t('homebase.clockProject.title')"
    :subtitle="step === 'activity' ? t('homebase.clockProject.activityStep') : subtitle"
    :close-label="t('homebase.clockProject.close')"
    data-testid="clock-project-sheet"
    @close="emit('close')"
  >
    <div v-if="step === 'project'" class="clock-sheet" :class="{ 'clock-sheet--searchable': searchable }">
      <Input
        v-if="searchable"
        v-model="query"
        type="search"
        class="clock-sheet__search"
        :placeholder="t('homebase.clockProject.search')"
        :aria-label="t('homebase.clockProject.search')"
        data-testid="clock-project-search"
      />
      <ul class="clock-sheet__list">
        <li v-if="!query.trim()">
          <button
            type="button"
            class="clock-sheet__row"
            :class="{ 'clock-sheet__row--selected': runningProjectId === null }"
            :aria-pressed="runningProjectId === null"
            :disabled="busy"
            data-testid="clock-project-none"
            @click="emit('pickNone')"
          >
            <span class="clock-sheet__swatch" aria-hidden="true" />
            <span class="clock-sheet__main">
              <span class="clock-sheet__name">{{ t('homebase.clockProject.none') }}</span>
              <span class="clock-sheet__sub">{{ t('homebase.clockProject.noneSub') }}</span>
            </span>
            <LumiIcon v-if="runningProjectId === null" name="done" class="clock-sheet__mark" aria-hidden="true" />
          </button>
        </li>
        <li v-for="project in shownProjects" :key="project.id">
          <button
            type="button"
            class="clock-sheet__row"
            :class="{ 'clock-sheet__row--selected': project.id === runningProjectId }"
            :style="{ '--row-color': projectColorVar(project.id) }"
            :aria-pressed="project.id === runningProjectId"
            :disabled="busy"
            data-testid="clock-project-option"
            :data-project-id="project.id"
            @click="choose(project)"
          >
            <span class="clock-sheet__dot" aria-hidden="true" />
            <span class="clock-sheet__main">
              <span class="clock-sheet__name clock-sheet__name--clip">{{ project.name }}</span>
              <span v-if="project.id === runningProjectId" class="clock-sheet__sub clock-sheet__sub--live">
                {{ runningLine }}
              </span>
            </span>
            <LumiIcon v-if="withActivityStep" name="next" class="clock-sheet__chevron" aria-hidden="true" />
            <LumiIcon v-else-if="project.id === runningProjectId" name="done" class="clock-sheet__mark" aria-hidden="true" />
          </button>
        </li>
      </ul>
      <EmptyState v-if="query.trim() && !shownProjects.length" quiet :title="t('homebase.clockProject.noMatch')" data-testid="clock-project-no-match" />
    </div>

    <div v-else class="clock-sheet">
      <Button variant="ghost" size="sm" class="clock-sheet__back" data-testid="clock-activity-back" @click="step = 'project'">
        <template #icon><LumiIcon name="previous" /></template>
        {{ t('homebase.clockProject.back') }}
      </Button>
      <ul class="clock-sheet__list" :style="{ '--row-color': chosen ? projectColorVar(chosen.id) : undefined }">
        <li v-for="activity in activities" :key="activity.id">
          <button
            type="button"
            class="clock-sheet__row"
            :class="{ 'clock-sheet__row--selected': activity.id === markedActivityId }"
            :aria-pressed="activity.id === markedActivityId"
            :disabled="busy"
            data-testid="clock-activity-option"
            :data-activity-id="activity.id"
            @click="chooseActivity(activity.id)"
          >
            <span class="clock-sheet__main">
              <span class="clock-sheet__name clock-sheet__name--clip">{{ activity.name }}</span>
            </span>
            <span v-if="activity.id === chosen?.defaultActivityId" class="clock-sheet__tag">{{ t('homebase.clockProject.defaultActivity') }}</span>
            <LumiIcon v-if="activity.id === markedActivityId" name="done" class="clock-sheet__mark" aria-hidden="true" />
          </button>
        </li>
      </ul>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, EmptyState, Input, LumiIcon, Modal } from '@lumi/ui';
import {
  PROJECT_SEARCH_FROM,
  filterProjects,
  needsActivityStep,
  projectColorVar,
  type ClockActivity,
  type ClockProject,
} from '@/utils/clockProjects';

const props = defineProps<{
  open: boolean;
  projects: ClockProject[];
  activities: ClockActivity[];
  /** The project timer running inside the open block; null = "Ohne Projekt". */
  runningProjectId: string | null;
  /** The running timer's Tätigkeit (its own, else the project's default). */
  runningActivityId: string | null;
  /** HH:MM the running project timer started. */
  runningSince: string;
  /** Under the title while picking a project, e.g. "Eingestempelt seit 09:46". */
  subtitle?: string;
  busy: boolean;
}>();

const emit = defineEmits<{
  close: [];
  pick: [projectId: string, activityId: string | null];
  pickNone: [];
}>();

const { t } = useI18n();

const step = ref<'project' | 'activity'>('project');
const chosen = ref<ClockProject | null>(null);
const query = ref('');

// Every opening starts at the project list with an empty search.
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    step.value = 'project';
    chosen.value = null;
    query.value = '';
  },
);

const searchable = computed(() => props.projects.length >= PROJECT_SEARCH_FROM);
const withActivityStep = computed(() => needsActivityStep(props.activities));
const shownProjects = computed(() => filterProjects(props.projects, query.value));

const runningLine = computed(() => {
  const activity = props.activities.find((a) => a.id === props.runningActivityId)?.name;
  return activity
    ? t('homebase.clockProject.runningWith', { activity, time: props.runningSince })
    : t('homebase.clockProject.runningSince', { time: props.runningSince });
});

// The row marked in step two: the running Tätigkeit when she reopens her running
// project, else the one the project books by default.
const markedActivityId = computed(() => {
  if (!chosen.value) return null;
  return chosen.value.id === props.runningProjectId ? props.runningActivityId : chosen.value.defaultActivityId;
});

function choose(project: ClockProject) {
  if (props.busy) return;
  if (withActivityStep.value) {
    chosen.value = project;
    step.value = 'activity';
    return;
  }
  // Pressed means running: a second press changes nothing.
  if (project.id === props.runningProjectId) {
    emit('close');
    return;
  }
  emit('pick', project.id, props.activities[0]?.id ?? null);
}

function chooseActivity(activityId: string) {
  const project = chosen.value;
  if (props.busy || !project) return;
  if (project.id === props.runningProjectId && activityId === props.runningActivityId) {
    emit('close');
    return;
  }
  emit('pick', project.id, activityId);
}
</script>

<style scoped>
.clock-sheet__search {
  margin-bottom: var(--space-3);
}
/* A sheet that can be searched keeps its height while the list narrows: the
   field and the first hits stay where they were, above the on-screen keyboard,
   instead of sinking behind it with a shrinking sheet. */
.clock-sheet--searchable {
  min-height: 24rem;
}
.clock-sheet__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
/* A thumb-sized row: 56 px at the least, the whole width the target. */
.clock-sheet__row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  min-height: 56px;
  padding: var(--space-2) var(--space-3);
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  font: inherit;
  color: var(--text-body);
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}
.clock-sheet__row:hover:not(:disabled),
.clock-sheet__row:active:not(:disabled) {
  background: var(--selected-bg);
}
.clock-sheet__row:disabled {
  cursor: progress;
  opacity: 0.55;
}
.clock-sheet__row:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.clock-sheet__row--selected {
  background: var(--surface-wash);
  box-shadow: inset 0 0 0 1.5px var(--row-color, var(--text-tertiary));
}
.clock-sheet__swatch,
.clock-sheet__dot {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.clock-sheet__swatch {
  border-radius: 4px;
  background: repeating-linear-gradient(135deg, transparent 0 2px, color-mix(in srgb, var(--text-tertiary) 45%, transparent) 2px 3.5px);
  box-shadow: inset 0 0 0 1px var(--border-strong);
}
.clock-sheet__dot {
  border-radius: var(--radius-pill);
  background: var(--row-color);
}
.clock-sheet__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.clock-sheet__name {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-heading);
}
.clock-sheet__name--clip {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.clock-sheet__sub {
  font-size: var(--text-xs);
  color: var(--text-tertiary);
}
.clock-sheet__sub--live {
  color: var(--success-text);
  font-weight: var(--weight-medium);
  font-variant-numeric: tabular-nums;
}
.clock-sheet__tag {
  flex-shrink: 0;
  padding: 2px var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--surface-wash);
  font-size: var(--text-xs);
  color: var(--text-secondary);
}
.clock-sheet__chevron,
.clock-sheet__mark {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: var(--text-tertiary);
}
.clock-sheet__mark {
  color: var(--success-text);
}
.clock-sheet__back {
  margin: 0 0 var(--space-2) calc(-1 * var(--space-1));
}
/* On a phone the names read at 16 px. */
@media (max-width: 767px) {
  .clock-sheet--searchable {
    min-height: 65vh;
    min-height: 65dvh;
  }
  .clock-sheet__name {
    font-size: var(--text-base);
  }
  .clock-sheet__sub {
    font-size: var(--text-label);
  }
}
</style>
